/**
 * Central TanStack Query cache-key registry.
 *
 * One source of truth — every read hook and every mutation's `invalidateQueries`
 * call goes through this module so no stringly-typed mismatches creep in.
 */
export const qk = {
  me: () => ['me'] as const,

  registrations: () => ['registrations'] as const,
  registration: (id: string) => ['registration', id] as const,

  orgs: (filters?: { status?: string }) => ['orgs', filters ?? {}] as const,
  org: (id: string) => ['org', id] as const,

  accounts: () => ['accounts'] as const,
  account: (externalSub: string) => ['account', externalSub] as const,
  platformStaff: () => ['platform-staff'] as const,
  platformStaffInvitations: () => ['platform-staff-invitations'] as const,

  agents: (orgId: string) => ['agents', orgId] as const,
  agent: (id: string) => ['agent', id] as const,

  capabilities: () => ['capabilities'] as const,
  capability: (id: string) => ['capability', id] as const,
  capabilitySchema: (id: string) => ['capability', id, 'schema'] as const,

  linkageTemplates: () => ['linkage-templates'] as const,
  linkageTemplate: (key: string) => ['linkage-template', key] as const,
  organizationLinkages: (orgId: string) => ['organization-linkages', orgId] as const,
  taskLinkage: (taskId: string) => ['task-linkage', taskId] as const,

  plugins: () => ['plugins'] as const,
  rollStatus: () => ['roll-status'] as const,
  pluginServers: () => ['plugin-servers'] as const,
  plugin: (name: string) => ['plugin', name] as const,
  pluginEnvironment: (name: string) => ['plugin-environment', name] as const,
  pluginEnvironmentJobs: (name: string) => ['plugin-environment-jobs', name] as const,
  environmentJobsActive: () => ['environment-jobs', 'active'] as const,

  serviceTokens: () => ['service-tokens'] as const,

  federation: () => ['federation'] as const,
  federationAudit: (filters?: Record<string, unknown>) =>
    ['federation-audit', filters ?? {}] as const,

  tasks: (filters?: Record<string, unknown>) => ['tasks', filters ?? {}] as const,
  task: (id: string) => ['task', id] as const,
  taskInput: (id: string) => ['task', id, 'input'] as const,
  taskOutput: (id: string) => ['task', id, 'output'] as const,
  taskProfile: (id: string) => ['task', id, 'profile'] as const,
  // What the plugin said while it ran; polled while the task is in flight.
  taskLogs: (id: string) => ['task', id, 'logs'] as const,
  // Under the task's key family so the task screen's invalidation covers it.
  taskWebhookDeliveries: (id: string) => ['task', id, 'webhook-deliveries'] as const,
  // What the run spent; under the task's family for the same reason.
  taskCost: (id: string) => ['task', id, 'cost'] as const,

  costRollups: (orgId: string, start: string, end: string) =>
    ['cost-rollups', orgId, start, end] as const,
  costEvents: (filters: Record<string, unknown>) => ['cost-events', filters] as const,
  costSummary: (start: string, end: string) => ['cost-summary', start, end] as const,
  rateCard: (start?: string, end?: string) => ['rate-card', start ?? '', end ?? ''] as const,
  reconciliation: (start: string, end: string) => ['reconciliation', start, end] as const,
  costChoices: () => ['cost-choices'] as const,
  fxRates: () => ['fx-rates'] as const,
  subscriptions: () => ['subscriptions'] as const,

  collections: (orgId: string) => ['collections', orgId] as const,
  orgDocuments: (orgId: string, filters?: Record<string, unknown>) =>
    ['org-documents', orgId, filters ?? {}] as const,
  orgRecords: (orgId: string, filters?: Record<string, unknown>) =>
    ['org-records', orgId, filters ?? {}] as const,
  collectionDocuments: (orgId: string, path: string) =>
    ['collection-documents', orgId, path] as const,

  orgUsers: (orgId: string) => ['org-users', orgId] as const,
  groups: (orgId: string) => ['groups', orgId] as const,
  groupCollections: (orgId: string, groupId: string) =>
    ['group-collections', orgId, groupId] as const,
  groupMembers: (orgId: string, groupId: string) => ['group-members', orgId, groupId] as const,
  memberGroups: (orgId: string, platformUserId: string) =>
    ['member-groups', orgId, platformUserId] as const,

  secretFields: () => ['secret-fields'] as const,
  orgSecrets: (orgId: string) => ['org-secrets', orgId] as const,
  /** The union of every agent's triggers — the tenant has no trigger list of its own. */
  globalSecrets: () => ['global-secrets'] as const,
  orgCapConfigs: (orgId: string) => ['org-cap-configs', orgId] as const,
  orgCapConfig: (orgId: string, capId: string) => ['org-cap-config', orgId, capId] as const,
  userCapConfig: (orgId: string, userId: string, capId: string) =>
    ['user-cap-config', orgId, userId, capId] as const,

  federatedPartners: (filters?: { status?: string }) =>
    ['federated-partners', filters ?? {}] as const,
  federatedPartner: (id: string) => ['federated-partner', id] as const,

  taskConcurrencySetting: () => ['platform-settings', 'task-concurrency'] as const,
  priorityDefault: () => ['platform-settings', 'capability-priority'] as const,
  capabilityPriorities: () => ['capability-priorities'] as const,
  settingFields: () => ['platform-settings', 'fields'] as const,

  organizationLinkageRuns: (orgId: string, key: string) =>
    ['organization-linkage-runs', orgId, key] as const,

  capabilityGlobalConfigs: () => ['capability-global-configs'] as const,
  capabilityGlobalConfig: (id: string) => ['capability-global-config', id] as const,

  partnerCapabilityConfigs: (partnerId: string) =>
    ['partner-capability-configs', partnerId] as const,
  partnerCapabilityConfig: (partnerId: string, id: string) =>
    ['partner-capability-config', partnerId, id] as const,
  partnerSecrets: (partnerId: string) => ['partner-secrets', partnerId] as const,

  webhookEndpoint: (kind: string, id: string) => ['webhook-endpoint', kind, id] as const,
  webhookDeliveries: (filters?: Record<string, unknown>) =>
    ['webhook-deliveries', filters ?? {}] as const,

  catalogList: (kind: string, filters?: Record<string, unknown>) =>
    ['catalog', kind, filters ?? {}] as const,
  catalogEntry: (kind: string, name: string) => ['catalog', kind, name] as const,
  installJobsByName: (name: string) => ['install-jobs', 'name', name] as const,
  installJob: (id: string) => ['install-jobs', id] as const,
  installJobsActive: () => ['install-jobs', 'active'] as const,
};
